import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import LoadingSpinner from "./LoadingSpinner";
import { getNameStyleCSS } from "./GameNameStyles";
import PublicProfileModal from "./PublicProfileModal";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 100;

const VIEW_TAPPERS = "tappers";
const VIEW_FIGHTERS = "fighters";
const VIEW_RECRUITERS = "recruiters";
const VIEW_ARENA_TROPHIES = "arena-trophies";
const VIEW_ARENA_WINS = "arena-wins";
const VIEW_TOURNAMENT = "tournament";

function normalizeView(value) {
  const view = String(value || "").trim().toLowerCase();

  if (view === "recruiters") {
    return VIEW_RECRUITERS;
  }

  if (
    view === "arena" ||
    view === "arena-trophies" ||
    view === "arena_trophies" ||
    view === "trophies"
  ) {
    return VIEW_ARENA_TROPHIES;
  }

  return VIEW_TAPPERS;
}

function svgFallback(name = "?") {
  const initial = (name?.[0] || "?").toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#444"/>
      <text x="50%" y="50%" dominant-baseline="middle"
            text-anchor="middle" font-size="32"
            fill="#fff" font-family="Arial, sans-serif">${initial}</text>
    </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function truncateUsername(value, max = 20) {
  const chars = Array.from(String(value ?? ""));
  return chars.length > max ? `${chars.slice(0, max).join("")}…` : chars.join("");
}

function cosmeticAssetSrc(value) {
  const src = String(value || "").trim();

  if (!src) return null;

  if (
    src.startsWith("/") ||
    src.startsWith("http://") ||
    src.startsWith("https://")
  ) {
    return src;
  }

  return `/name-cosmetics/${src}`;
}

function LeaderboardTab({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        minWidth: 92,
        height: 46,
        padding: "0 6px",
        margin: 0,
        boxSizing: "border-box",

        background: active
          ? "rgba(112, 61, 27, 0.96)"
          : "rgba(95, 49, 18, 0.76)",

        border: active
          ? "2px solid rgba(135, 78, 36, 0.98)"
          : "2px solid rgba(70, 32, 10, 0.95)",

        borderRadius: 7,

        boxShadow: active
          ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
          : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

        color: "#fff",
        fontSize: 16,
        lineHeight: "42px",
        fontWeight: 600,
        textAlign: "center",
        textTransform: "uppercase",

        textShadow:
          "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",

        cursor: "pointer",
        appearance: "none",
        WebkitAppearance: "none",
        opacity: active ? 1 : 0.88,
        fontFamily: '"Fredoka", sans-serif',
      }}
    >
      {label}
    </button>
  );
}

export default function Leaderboard({
  user,
  onNavigate,
  openedVia = "menu",
  initialView,
  arenaId: initialArenaId,
  arenaIndex: initialArenaIndex,
  viewOnly = false,
  tournamentEnabled,
  tournamentStartDate,
  tournamentEndDate,
  activeTournamentId,
}) {
  const { season: activeSeasonObj, tournamentsActive } = useAppConfig();

  const activeSeasonNumber = activeSeasonObj?.season_number ?? null;
  const activeSeasonEndISO = activeSeasonObj?.end_date ?? null;

  const activeTournament = useMemo(() => {
    const list = Array.isArray(tournamentsActive) ? tournamentsActive : [];
    return list[0] ?? null;
  }, [tournamentsActive]);

  const tournamentFromISO =
    tournamentStartDate ??
    activeTournament?.startAt ??
    activeTournament?.start_at ??
    null;

  const tournamentToISO =
    tournamentEndDate ??
    activeTournament?.endAt ??
    activeTournament?.end_at ??
    null;

  const tournamentTabEnabled = Boolean(
    tournamentEnabled ||
      tournamentFromISO ||
      activeTournamentId ||
      activeTournament
  );

  const [view, setView] = useState(() => normalizeView(initialView));
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [seasons, setSeasons] = useState([]);
const [selectedSeason, setSelectedSeason] = useState(activeSeasonNumber);
const [seasonsLoading, setSeasonsLoading] = useState(true);

const [arenas, setArenas] = useState([]);
const [selectedArena, setSelectedArena] = useState(null);
const [arenasLoading, setArenasLoading] = useState(true);

  const [error, setError] = useState("");
  const [myRank, setMyRank] = useState(null);
  const [myRankLoading, setMyRankLoading] = useState(false);
  const [myRankError, setMyRankError] = useState("");
  const [publicProfileUserId, setPublicProfileUserId] = useState(null);

  const [tournamentRankBy, setTournamentRankBy] = useState("wins");
  const [tournamentSide, setTournamentSide] = useState("all");

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
  if (!user?.id) return;

  setEntries((currentEntries) =>
    currentEntries.map((entry) => {
      if (String(entry.id) !== String(user.id)) {
        return entry;
      }

      return {
        ...entry,

        username:
          user?.name ??
          user?.username ??
          entry.username,

        avatar:
          user?.avatarUrl ??
          user?.avatar_url ??
          user?.photo_url ??
          user?.avatar ??
          entry.avatar,

        gamepassActive:
  Boolean(
    user?.gamepass_active ??
    user?.gamepassActive ??
    entry.gamepassActive
  ) &&
  Number(
    user?.gamepass_season_id ??
    user?.gamepassSeasonId ??
    user?.game_pass_season_id
  ) ===
    Number(selectedSeason),

        activeNameStyle:
          user?.active_name_style ??
          user?.activeNameStyle ??
          null,

        activeNameBanner:
          user?.active_name_banner ??
          user?.activeNameBanner ??
          null,

        activeNameEmogy:
          user?.active_name_emogy ??
          user?.activeNameEmogy ??
          null,
      };
    })
  );
}, [
  user?.id,
  user?.name,
  user?.username,
  user?.avatarUrl,
  user?.avatar_url,
  user?.photo_url,
  user?.avatar,
  user?.gamepass_active,
user?.gamepassActive,
user?.gamepass_season_id,
user?.gamepassSeasonId,
user?.game_pass_season_id,
selectedSeason,
user?.active_name_style,
  user?.activeNameStyle,
  user?.active_name_banner,
  user?.activeNameBanner,
  user?.active_name_emogy,
  user?.activeNameEmogy,
]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const isArenaView =
    view === VIEW_ARENA_TROPHIES || view === VIEW_ARENA_WINS;

  useEffect(() => {
    if (initialView) {
      setView(normalizeView(initialView));
    }
  }, [initialView]);

  useEffect(() => {
    if (view === VIEW_TOURNAMENT && !tournamentTabEnabled) {
      setView(VIEW_TAPPERS);
    }
  }, [view, tournamentTabEnabled]);

  useEffect(() => {
    if (!activeSeasonEndISO) return undefined;

    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 30000);

    return () => window.clearInterval(interval);
  }, [activeSeasonEndISO]);

  const endsIn = useMemo(() => {
    if (
      !activeSeasonEndISO ||
      selectedSeason !== activeSeasonNumber
    ) {
      return null;
    }

    const end = new Date(activeSeasonEndISO).getTime();
    const remaining = end - now;

    if (!Number.isFinite(remaining) || remaining <= 0) {
      return "Ended";
    }

    const days = Math.floor(remaining / (24 * 3600000));
    const hours = Math.floor((remaining % (24 * 3600000)) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);

    return `${days}D ${hours}H ${minutes}M`;
  }, [
    activeSeasonEndISO,
    activeSeasonNumber,
    now,
    selectedSeason,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadSeasons() {
      try {
        const response = await fetch(
          `${API_BASE}/api/season-stats/seasons`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const values = Array.isArray(data?.seasons)
          ? data.seasons.map(Number).filter(Number.isFinite)
          : [];

        if (!cancelled) {
          setSeasons([...new Set(values)].sort((a, b) => b - a));
        }
      } catch {
  if (!cancelled && activeSeasonNumber != null) {
    setSeasons([activeSeasonNumber]);
  }
} finally {
  if (!cancelled) {
    setSeasonsLoading(false);
  }
}
    }

    loadSeasons();

    return () => {
      cancelled = true;
    };
  }, [activeSeasonNumber]);

  useEffect(() => {
    if (activeSeasonNumber != null && selectedSeason == null) {
      setSelectedSeason(activeSeasonNumber);
    }
  }, [activeSeasonNumber, selectedSeason]);

  useEffect(() => {
    let cancelled = false;

    async function loadArenas() {
      try {
        const response = await fetch(`${API_BASE}/api/arenas/arenas`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.arenas)
            ? data.arenas
            : [];

        const cleaned = raw
          .map((arena) => ({
            id: String(arena?.id ?? arena?.arena_id ?? arena),
            name:
              arena?.name ??
              arena?.title ??
              String(arena?.id ?? arena),
          }))
          .filter(
            (arena) =>
              arena.id &&
              arena.id !== "null" &&
              arena.id !== "undefined"
          );

        if (!cancelled) {
          setArenas(cleaned);
        }
      } catch {
  if (!cancelled) {
    setArenas([]);
  }
} finally {
  if (!cancelled) {
    setArenasLoading(false);
  }
}
    }

    loadArenas();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!arenas.length) return;

    if (initialArenaId != null) {
      setSelectedArena(String(initialArenaId));
      return;
    }

    setSelectedArena((current) => current || String(arenas[0].id));
  }, [arenas, initialArenaId]);

  useEffect(() => {
    setPage(1);
  }, [
    view,
    selectedSeason,
    selectedArena,
    tournamentRankBy,
    tournamentSide,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadLeaderboard() {
      const offset = (page - 1) * PAGE_SIZE;

      if (selectedSeason == null && view !== VIEW_TOURNAMENT) {
        setEntries([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      if (isArenaView && !selectedArena) {
        setEntries([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        let url = "";

        if (view === VIEW_TOURNAMENT) {
          if (!tournamentFromISO) {
            setEntries([]);
            setTotal(0);
            setError("No tournament window configured.");
            return;
          }

          const params = new URLSearchParams({
            from: tournamentFromISO,
            ...(tournamentToISO ? { to: tournamentToISO } : {}),
            side: tournamentSide,
            rankBy: tournamentRankBy,
            limit: String(PAGE_SIZE),
            offset: String(offset),
          });

          url = `${API_BASE}/api/tournament/stats?${params.toString()}`;
        } else if (view === VIEW_RECRUITERS) {
          url =
            `${API_BASE}/api/season-stats/recruiters-leaderboard` +
            `?season=${encodeURIComponent(selectedSeason)}` +
            `&limit=${PAGE_SIZE}&offset=${offset}`;
        } else if (view === VIEW_ARENA_TROPHIES) {

  url =
    `${API_BASE}/api/season-stats/trophies-leaderboard` +
    `?limit=${PAGE_SIZE}&offset=${offset}`;

} else if (view === VIEW_ARENA_WINS) {

  url =
    `${API_BASE}/api/arenas/leaderboard` +
    `?season=${encodeURIComponent(selectedSeason)}` +
    `&arenaId=${encodeURIComponent(selectedArena)}` +
    `&metric=wins` +
    `&limit=${PAGE_SIZE}&offset=${offset}`;
        } else {
          const metric =
            view === VIEW_FIGHTERS ? "wins" : "taps";

          url =
            `${API_BASE}/api/season-stats/leaderboard` +
            `?season=${encodeURIComponent(selectedSeason)}` +
            `&metric=${metric}` +
            `&limit=${PAGE_SIZE}&offset=${offset}`;
        }

        let response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.status === 404 && view !== VIEW_TOURNAMENT) {
          if (view === VIEW_RECRUITERS) {
            url =
              `${API_BASE}/api/season-stats/recruiters-leaderboard/` +
              `${encodeURIComponent(selectedSeason)}` +
              `?limit=${PAGE_SIZE}&offset=${offset}`;
          } else if (view === VIEW_ARENA_TROPHIES) {

  url =
    `${API_BASE}/api/season-stats/trophies-leaderboard` +
    `?limit=${PAGE_SIZE}&offset=${offset}`;

} else if (view === VIEW_ARENA_WINS) {

  url =
    `${API_BASE}/api/arenas/leaderboard/` +
    `${encodeURIComponent(selectedSeason)}` +
    `?arenaId=${encodeURIComponent(selectedArena)}` +
    `&metric=wins` +
    `&limit=${PAGE_SIZE}&offset=${offset}`;
          } else {
            const metric =
              view === VIEW_FIGHTERS ? "wins" : "taps";

            url =
              `${API_BASE}/api/season-stats/leaderboard/` +
              `${encodeURIComponent(selectedSeason)}` +
              `?metric=${metric}` +
              `&limit=${PAGE_SIZE}&offset=${offset}`;
          }

          response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];

        const cleaned = raw.map((entry) => ({
          id: entry.id ?? entry.user_id ?? entry.userId,
          username: entry.username ?? entry.name ?? "Unknown",
          taps: Number(entry.taps ?? 0),
          trophies: Number(entry.trophies ?? 0),
          wins: Number(entry.wins ?? 0),
          losses: Number(entry.losses ?? 0),
          battles: Number(entry.battles ?? 0),
          recruits: Number(entry.recruits ?? 0),
          avatar: entry.avatarUrl ?? entry.avatar_url,
          earlyBird: entry.early_bird,
          gamepassActive: entry.gamepass_active,
          battlepassActive: entry.battlepass_active,
          side: entry.side,
          activeNameStyle:
            entry.active_name_style ?? entry.activeNameStyle ?? null,
          activeNameBanner:
            entry.active_name_banner ?? entry.activeNameBanner ?? null,
          activeNameEmogy:
            entry.active_name_emogy ?? entry.activeNameEmogy ?? null,
        }));

        if (!cancelled) {
          setEntries(cleaned);

          const apiTotal = Number(
            Array.isArray(data) ? 0 : data?.total ?? 0
          );

          if (Number.isFinite(apiTotal) && apiTotal > 0) {
            setTotal(apiTotal);
          } else {
            setTotal(offset + cleaned.length);
          }
        }
      } catch (loadError) {
        console.error("[UnifiedLeaderboard] load failed:", loadError);

        if (!cancelled) {
          setEntries([]);
          setTotal(0);
          setError("Could not load leaderboard.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, [
    isArenaView,
    page,
    selectedArena,
    selectedSeason,
    tournamentFromISO,
    tournamentRankBy,
    tournamentSide,
    tournamentToISO,
    view,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadMyRank() {
      if (selectedSeason == null && view !== VIEW_TOURNAMENT) {
        setMyRank(null);
        return;
      }

      if (isArenaView && !selectedArena) {
        setMyRank(null);
        return;
      }

      setMyRankLoading(true);
      setMyRankError("");

      try {
        let url = "";

        if (view === VIEW_TOURNAMENT) {
          if (!tournamentFromISO) {
            setMyRank(null);
            return;
          }

          const params = new URLSearchParams({
            from: tournamentFromISO,
            ...(tournamentToISO ? { to: tournamentToISO } : {}),
            side: tournamentSide,
            rankBy: tournamentRankBy,
          });

          url = `${API_BASE}/api/tournament/my-rank?${params.toString()}`;
        } else if (view === VIEW_RECRUITERS) {
          url =
            `${API_BASE}/api/season-stats/recruiters-leaderboard/my-rank` +
            `?season=${encodeURIComponent(selectedSeason)}`;
        } else if (view === VIEW_ARENA_TROPHIES) {

  url =
    `${API_BASE}/api/season-stats/trophies-leaderboard/my-rank`;

} else if (view === VIEW_ARENA_WINS) {

  url =
    `${API_BASE}/api/arenas/leaderboard/my-rank` +
    `?season=${encodeURIComponent(selectedSeason)}` +
    `&arenaId=${encodeURIComponent(selectedArena)}` +
    `&metric=wins`;
        } else {
          const metric =
            view === VIEW_FIGHTERS ? "wins" : "taps";

          url =
            `${API_BASE}/api/season-stats/leaderboard/my-rank` +
            `?season=${encodeURIComponent(selectedSeason)}` +
            `&metric=${metric}`;
        }

        let response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.status === 404 && view === VIEW_RECRUITERS) {
          response = await fetch(
            `${API_BASE}/api/season-stats/recruiters-leaderboard/my-rank/` +
              `${encodeURIComponent(selectedSeason)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled) {
          setMyRank(
            data?.rank != null
              ? data
              : {
                  ...data,
                  rank: null,
                }
          );
        }
      } catch (rankError) {
        console.error("[UnifiedLeaderboard] rank failed:", rankError);

        if (!cancelled) {
          setMyRank(null);
          setMyRankError("Could not load your rank.");
        }
      } finally {
        if (!cancelled) {
          setMyRankLoading(false);
        }
      }
    }

    loadMyRank();

    return () => {
      cancelled = true;
    };
  }, [
    isArenaView,
    selectedArena,
    selectedSeason,
    tournamentFromISO,
    tournamentRankBy,
    tournamentSide,
    tournamentToISO,
    view,
  ]);

  const arenaName = useMemo(() => {
    const arena = arenas.find(
      (item) => String(item.id) === String(selectedArena)
    );

    return arena?.name || "Arena";
  }, [arenas, selectedArena]);

  const title = useMemo(() => {
    if (view === VIEW_TAPPERS) return "Top Tappers";
    if (view === VIEW_FIGHTERS) return "Top Fighters";
    if (view === VIEW_RECRUITERS) return "Top Recruiters";
    if (view === VIEW_ARENA_TROPHIES) {
  return "Top Trophies";
}
    if (view === VIEW_ARENA_WINS) {
      return `${arenaName} Top Fighters`;
    }

    return "Tournament Stats";
  }, [arenaName, view]);

  function getEntryCount(entry) {
    if (view === VIEW_TAPPERS) return entry.taps;
    if (view === VIEW_FIGHTERS) return entry.wins;
    if (view === VIEW_RECRUITERS) return entry.recruits;
    if (view === VIEW_ARENA_TROPHIES) return entry.trophies;
    if (view === VIEW_ARENA_WINS) return entry.wins;

    return tournamentRankBy === "battles"
      ? entry.battles
      : entry.wins;
  }

  function getAmountIcon() {
    if (view === VIEW_TAPPERS) return "/taps.png";
    if (view === VIEW_RECRUITERS) return "/recruits.png";
    if (view === VIEW_ARENA_TROPHIES) return "/v2/trophy.png";
    return "/wins.png";
  }

  function getRankValue() {
    if (!myRank) return 0;

    if (view === VIEW_TAPPERS) return myRank.taps ?? 0;
    if (view === VIEW_FIGHTERS) return myRank.wins ?? 0;
    if (view === VIEW_RECRUITERS) return myRank.recruits ?? 0;
    if (view === VIEW_ARENA_TROPHIES) return myRank.trophies ?? 0;
    if (view === VIEW_ARENA_WINS) return myRank.wins ?? 0;

    return tournamentRankBy === "battles"
      ? myRank.battles ?? 0
      : myRank.wins ?? 0;
  }

  function getRankUnit() {
    if (view === VIEW_TAPPERS) return "Taps";
    if (view === VIEW_RECRUITERS) return "Recruits";
    if (view === VIEW_ARENA_TROPHIES) return "Trophies";
    if (view === VIEW_TOURNAMENT && tournamentRankBy === "battles") {
      return "Battles";
    }

    return "Wins";
  }

  function getEmptyRankText() {
    if (view === VIEW_TAPPERS) return "No rank yet.";
    if (view === VIEW_RECRUITERS) {
      return "No rank yet — refer at least one player to enter this board.";
    }
    if (view === VIEW_ARENA_TROPHIES) {
  return "No rank yet — earn trophies to enter this board.";
}
    if (view === VIEW_ARENA_WINS) {
      return "No rank yet — win at least one arena fight.";
    }

    return "No rank yet — win at least one fight to enter this board.";
  }

  const amountIcon = getAmountIcon();

  const pageLoading =
  loading ||
  myRankLoading ||
  seasonsLoading ||
  (isArenaView && arenasLoading);

if (pageLoading) {
  return (
    <div
      className="shop-container"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoadingSpinner
        text="Loading Leaderboard"
        size={64}
      />
    </div>
  );
}

return (


  <>
    <div className="shop-container" style={{ marginTop: -8 }}>
      <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    width: "100%",
    gap: 8,
    alignItems: "stretch",
    marginBottom: 12,
    overflowX: "auto",
    paddingBottom: 4,
  }}
>
  <LeaderboardTab
    active={view === VIEW_TAPPERS}
    label="TAPPERS"
    onClick={() => setView(VIEW_TAPPERS)}
  />

  <LeaderboardTab
    active={view === VIEW_RECRUITERS}
    label="INVITES"
    onClick={() => setView(VIEW_RECRUITERS)}
  />

  <LeaderboardTab
    active={view === VIEW_ARENA_TROPHIES}
    label="ARENAS"
    onClick={() => setView(VIEW_ARENA_TROPHIES)}
  />
</div>

      <div
        className="card-grid"
        style={{
          marginTop: -15,
          marginLeft: -10,
          marginRight: -10,
          width: "calc(100% + 20px)",
        }}
      >
        <div className="modal_title">{title}</div>

        <div className="modal_body modal_body_2">
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "center",
              gap: 10,
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            {isArenaView && arenas.length > 1000 && (
              <select
                value={selectedArena ?? ""}
                onChange={(event) =>
                  setSelectedArena(event.target.value || null)
                }
                className="form-select"
                style={{
                  marginBottom: -10,
                  marginTop: 5,
                  borderRadius: 8,
                  width: 220,
                  height: 36,
                  padding: "4px 8px",
                }}
              >
                {arenas.map((arena) => (
                  <option key={arena.id} value={arena.id}>
                    {arena.name}
                  </option>
                ))}
              </select>
            )}

            {seasons.length > 0 && view !== VIEW_TOURNAMENT && (
              <select
                value={selectedSeason ?? ""}
                onChange={(event) =>
                  setSelectedSeason(
                    event.target.value
                      ? Number(event.target.value)
                      : null
                  )
                }
                className="form-select"
                style={{
                  display: "none",
                  borderRadius: 8,
                  width: 200,
                  height: 36,
                  padding: "4px 8px",
                }}
              >
                {seasons.map((season) => (
                  <option key={season} value={season}>
                    Season {season}
                    {season === activeSeasonNumber ? " (current)" : ""}
                  </option>
                ))}
              </select>
            )}

            {view === VIEW_TOURNAMENT && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <select
                  value={tournamentRankBy}
                  onChange={(event) =>
                    setTournamentRankBy(
                      event.target.value === "battles"
                        ? "battles"
                        : "wins"
                    )
                  }
                  className="form-select"
                >
                  <option value="wins">Rank by Wins</option>
                  <option value="battles">Rank by Battles</option>
                </select>

                <select
                  value={tournamentSide}
                  onChange={(event) => {
                    const next = event.target.value;
                    setTournamentSide(
                      next === "good" || next === "bad"
                        ? next
                        : "all"
                    );
                  }}
                  className="form-select"
                >
                  <option value="all">All Sides</option>
                  <option value="good">Sun Pharaohs</option>
                  <option value="bad">Moon Anubis</option>
                </select>
              </div>
            )}

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  minHeight: 32,
                  padding: "3px 12px",
                  borderRadius: 6,
                  background: "rgba(0,0,0,0.35)",
                  border: "1px solid #555",
                  color: "#ffcc62",
                  textShadow: "1px 1px #000",
                  fontSize: 16,
                }}
              >
                {myRankError ? (
                  <span>{myRankError}</span>
                ) : myRank?.rank != null ? (
                  <>
                    <span>
                      <b>Your Rank:</b>{" "}
                      <span
                        style={{
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        #{myRank.rank}
                      </span>
                    </span>

                    <span style={{ opacity: 0.8 }}>
                      ({getRankValue()} {getRankUnit()})
                    </span>
                  </>
                ) : (
                  <span>{getEmptyRankText()}</span>
                )}
              </div>
            </div>

            {selectedSeason === activeSeasonNumber && endsIn && (
              <div style={{ display: "none" }}>
                Ends in: {endsIn}
              </div>
            )}

            {error && (
              <div
                style={{
                  color: "#ff8080",
                  marginTop: 10,
                  textShadow: "1px 1px #000",
                }}
              >
                {error}
              </div>
            )}

          {entries.length === 0 && !error ? (
              <div
                className="shadow1"
                style={{
                  width: "100%",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 16,
                }}
              >
                No leaderboard entries yet.
              </div>
            ) : (
              entries.map((entry, index) => {
                const globalIndex =
                  (page - 1) * PAGE_SIZE + index;

                const rankLabel =
                  globalIndex === 0
                    ? "/1.png"
                    : globalIndex === 1
                      ? "/2.png"
                      : globalIndex === 2
                        ? "/3.png"
                        : `#${globalIndex + 1}`;

                const rankFontSize =
                  String(rankLabel).length > 4
                    ? 14
                    : String(rankLabel).length > 3
                      ? 16
                      : 20;

                const bannerSrc = cosmeticAssetSrc(
                  entry.activeNameBanner
                );
                const emojiSrc = cosmeticAssetSrc(
                  entry.activeNameEmogy
                );
                const nameStyle = getNameStyleCSS(
                  entry.activeNameStyle
                );

                const hasNameStyle = Object.keys(nameStyle).length > 0;

                return (
                  <motion.div
                    key={entry.id ?? index}
                    className="task-card"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.03,
                    }}
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 5px 10px 5px",
                      cursor: "default",
                      margin: 0,
                      backgroundImage: bannerSrc
                        ? `linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.28)), url('${bannerSrc}')`
                        : undefined,
                      backgroundSize: bannerSrc
                        ? "cover"
                        : undefined,
                      backgroundPosition: "center",
                    }}
                  >
                    <span
                      style={{
                        width: 32,
                        minWidth: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: rankFontSize,
                      }}
                    >
                      {globalIndex <= 2 ? (
                        <img
                          src={rankLabel}
                          alt={`Rank ${globalIndex + 1}`}
                          draggable={false}
                          style={{
                            width: 32,
                            height: 32,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        rankLabel
                      )}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          flex: "0 0 32px",
                          borderRadius: 8,
                          overflow: "hidden",
                          position: "relative",
                          background: "#000",
                        }}
                      >
                        <img
                          src={
                            entry.avatar ||
                            svgFallback(entry.username)
                          }
                          alt=""
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 8,
                            boxSizing: "border-box",
                            border: entry.earlyBird
                              ? "1px solid #FFD700"
                              : 0,
                          }}
                        />

                        {entry.earlyBird && (
                          <img
                            src="/whitelist.png"
                            alt=""
                            title="Whitelisted"
                            style={{
                              position: "absolute",
                              right: 1,
                              bottom: 1,
                              width: 15,
                              height: 15,
                            }}
                          />
                        )}

                      </div>

                      <span
  title={`View ${entry.username}'s profile`}
  role="button"
  tabIndex={0}
  onClick={(event) => {
    event.stopPropagation();

    if (entry?.id != null) {
      setPublicProfileUserId(entry.id);
    }
  }}
  onKeyDown={(event) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      event.stopPropagation();

      if (entry?.id != null) {
        setPublicProfileUserId(entry.id);
      }
    }
  }}
  style={{
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    overflow: "hidden",
    fontSize: 18,
    textAlign: "left",
    cursor: "pointer",
  }}
>
{(entry.gamepassActive || entry.battlepassActive) && (
  <img
    src={entry.battlepassActive ? "/pass2.png" : "/pass1.png"}
    alt=""
    draggable={false}
    title={entry.battlepassActive ? "OG Pass Plus" : "OG Pass"}
    style={{
      width: 24,
      height: 24,
      flex: "0 0 24px",
      objectFit: "contain",
    }}
  />
)}

{emojiSrc && (
  <img
    src={emojiSrc}
    alt=""
    draggable={false}
    style={{
      width: 22,
      height: 22,
      flex: "0 0 22px",
      objectFit: "contain",
      borderRadius: "6px",
    }}
  />
)}

<span
  style={{
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",

    color: "#fff",

    textShadow: hasNameStyle
      ? "none"
      : "1px 1px #000",

    ...nameStyle,
  }}
>
  {truncateUsername(entry.username)}
</span>
</span>
                    </div>

                    <div
                      className="amount"
                      style={{
                        flexShrink: 0,
                        marginLeft: 8,
                        paddingRight: 8,
                        paddingLeft:
                          view === VIEW_FIGHTERS ||
                          view === VIEW_ARENA_WINS ||
                          view === VIEW_TOURNAMENT
                            ? 34
                            : 26,
                        color: "#fff",
                        fontSize: 18,
                        textShadow: "1px 1px #000",
                        backgroundImage: `url('${amountIcon}')`,
                        backgroundSize:
                          view === VIEW_FIGHTERS ||
                          view === VIEW_ARENA_WINS ||
                          view === VIEW_TOURNAMENT
                            ? "30px"
                            : "21px 21px",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "left center",
                      }}
                    >
                      {getEntryCount(entry)}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          <div
  style={{
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  }}
>
  <button
    type="button"
    disabled={page <= 1}
    onClick={() =>
      setPage((current) => Math.max(1, current - 1))
    }
    style={{
      width: 44,
      height: 44,
      flex: "0 0 44px",
      border: 0,
      padding: 0,
      margin: 0,
      background: "transparent",
      opacity: page <= 1 ? 0.45 : 1,
      cursor: page <= 1 ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/v2/left.png"
      alt="Previous"
      draggable={false}
      style={{
        width: 44,
        height: 44,
        display: "block",
        objectFit: "contain",
        pointerEvents: "none",
      }}
    />
  </button>

  <div
    style={{
      height: 44,
      minWidth: 120,
      padding: "0 18px",
      borderRadius: 8,
      background: "rgba(0,0,0,0.45)",
      border: "2px solid #7f5031",
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      textAlign: "center",
      textTransform: "uppercase",
      textShadow: "1px 1px #000",
      boxSizing: "border-box",
      whiteSpace: "nowrap",
      fontFamily: '"Fredoka", sans-serif',
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    Page {page}{" "}
    <span style={{ color: "#ffdf5f", marginLeft: 4 }}>
      / {totalPages}
    </span>
  </div>

  <button
    type="button"
    disabled={page >= totalPages}
    onClick={() =>
      setPage((current) =>
        Math.min(totalPages, current + 1)
      )
    }
    style={{
      width: 44,
      height: 44,
      flex: "0 0 44px",
      border: 0,
      padding: 0,
      margin: 0,
      background: "transparent",
      opacity: page >= totalPages ? 0.45 : 1,
      cursor:
        page >= totalPages
          ? "not-allowed"
          : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/v2/right.png"
      alt="Next"
      draggable={false}
      style={{
        width: 44,
        height: 44,
        display: "block",
        objectFit: "contain",
        pointerEvents: "none",
      }}
    />
  </button>
</div>
                </div>
      </div>
    </div>

    {publicProfileUserId != null && (
      <PublicProfileModal
  userId={publicProfileUserId}
  onClose={() => setPublicProfileUserId(null)}
  onNavigate={onNavigate}
/>
    )}
  </>
);
}